import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play, Pause, RotateCcw, ArrowRight, Loader2, CheckCircle2, XCircle, AlertTriangle,
  MinusCircle, Sparkles, Shield, Calculator as CalcIcon, FileSearch, GitBranch, UserCheck,
  RefreshCcw, MessageSquare,
} from "lucide-react";
import Logo from "@/components/Logo";
import BrandStripe from "@/components/BrandStripe";
import { Divider } from "@/components/Divider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

const AGENT_ICONS = {
  1: MessageSquare,
  2: FileSearch,
  3: Shield,
  4: CalcIcon,
  5: GitBranch,
  6: Sparkles,
  7: UserCheck,
  8: RefreshCcw,
};
const STATUS_ICONS = {
  ok: { Icon: CheckCircle2, c: "#00A651" },
  warn: { Icon: AlertTriangle, c: "#E88C2F" },
  fail: { Icon: XCircle, c: "#ED1C24" },
  terminal: { Icon: XCircle, c: "#ED1C24" },
  skipped: { Icon: MinusCircle, c: "#9CA3AF" },
  pending: { Icon: Loader2, c: "#6B7280" },
};

export default function Demo() {
  const [presets, setPresets] = useState([]);
  const [borrower, setBorrower] = useState({
    first_name: "Maya", last_name: "Wilson",
    annual_income: 110000, monthly_debts: 1200,
    self_reported_credit: 720, property_value: 425000,
    loan_amount: 340000, document_authenticity: "passed",
    employment_status: "employed", target_program: "conventional",
  });
  const [result, setResult] = useState(null);
  const [playedIdx, setPlayedIdx] = useState(-1); // last event index revealed
  const [playing, setPlaying] = useState(false);
  const [activePreset, setActivePreset] = useState("ready");
  const timerRef = useRef(null);

  useEffect(() => {
    api.get("/pipeline/presets").then((r) => setPresets(r.data?.presets || []));
  }, []);

  const setField = (k, v) => setBorrower((b) => ({ ...b, [k]: v }));

  const pickPreset = (p) => {
    setActivePreset(p.id);
    setBorrower(p.borrower);
    setResult(null);
    setPlayedIdx(-1);
    stopPlayback();
  };

  const stopPlayback = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setPlaying(false);
  };

  const playSequence = (events, startFrom = 0) => {
    setPlaying(true);
    const advance = (i) => {
      if (i >= events.length) { setPlaying(false); return; }
      const ev = events[i];
      // Min 600ms per step for legibility, scaled from backend duration_ms
      const dur = Math.max(600, Math.min(1800, ev.duration_ms || 700));
      setPlayedIdx(i);
      timerRef.current = setTimeout(() => advance(i + 1), dur);
    };
    advance(startFrom);
  };

  const runDemo = async () => {
    stopPlayback();
    setPlayedIdx(-1);
    try {
      const r = await api.post("/pipeline/demo", borrower);
      setResult(r.data);
      // small delay so the UI can transition
      setTimeout(() => playSequence(r.data.events, 0), 250);
    } catch (e) {
      console.error(e);
    }
  };

  const replay = () => {
    if (!result) return;
    stopPlayback();
    setPlayedIdx(-1);
    setTimeout(() => playSequence(result.events, 0), 200);
  };

  useEffect(() => () => stopPlayback(), []);

  // Live computed firewall metrics for the input panel preview
  const monthlyIncome = (Number(borrower.annual_income) || 0) / 12;
  const dti = monthlyIncome > 0 ? (Number(borrower.monthly_debts) / monthlyIncome) * 100 : 0;
  const ltv = Number(borrower.property_value) > 0 ? (Number(borrower.loan_amount) / Number(borrower.property_value)) * 100 : 0;
  const willPassFirewall = useMemo(() => {
    return borrower.document_authenticity !== "failed"
      && dti <= 55
      && Number(borrower.self_reported_credit) >= 500;
  }, [borrower, dti]);

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]" data-testid="page-demo">
      <BrandStripe thickness="h-1.5" />
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="demo-back-home"><Logo size={36} /></Link>
          <div className="hidden md:flex items-center gap-3 text-xs uppercase tracking-wider font-bold">
            <Link to="/ecosystem" className="text-gray-500 hover:text-black">Explainer</Link>
            <span className="text-gray-300">·</span>
            <span className="text-[#00A651]">Demo Mode</span>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {/* INTRO */}
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#00A651]">8-Agent Pipeline · Live Demo</p>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-black mt-2 tracking-tight max-w-3xl">
            Run a borrower through the firewall.
          </h1>
          <Divider className="my-5" />
          <p className="font-sub italic text-lg text-gray-700 max-w-3xl">
            Pick a profile or build your own. Agent 5 — the financial gatekeeper — decides whether to spend premium API dollars or route the lead to nurture.
          </p>

          {/* PRESETS */}
          <div className="mt-8 flex flex-wrap gap-3" data-testid="demo-presets">
            {presets.map((p) => (
              <button
                key={p.id}
                onClick={() => pickPreset(p)}
                data-testid={`preset-${p.id}`}
                className={`px-4 py-2 text-xs uppercase tracking-wider font-bold rounded-sm transition-colors border ${
                  activePreset === p.id
                    ? "bg-black text-white border-black"
                    : "bg-white text-black border-gray-200 hover:border-black"
                }`}
                title={p.description}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* MAIN PANEL */}
          <div className="grid lg:grid-cols-12 gap-8 mt-8">
            {/* LEFT — borrower inputs */}
            <section className="lg:col-span-4">
              <div className="bg-white border border-gray-200 p-6 rounded-sm sticky top-6">
                <h3 className="font-heading font-bold text-lg text-black">Borrower Profile</h3>
                <Divider className="my-3" />

                <div className="grid grid-cols-2 gap-3">
                  <Field label="First" value={borrower.first_name} onChange={(v) => setField("first_name", v)} testid="demo-first" />
                  <Field label="Last" value={borrower.last_name} onChange={(v) => setField("last_name", v)} testid="demo-last" />
                </div>

                <NumField label="Annual Income" value={borrower.annual_income} onChange={(v) => setField("annual_income", v)} testid="demo-income" />
                <NumField label="Monthly Debts" value={borrower.monthly_debts} onChange={(v) => setField("monthly_debts", v)} testid="demo-debts" />
                <NumField label="Self-Reported Credit" value={borrower.self_reported_credit} onChange={(v) => setField("self_reported_credit", v)} testid="demo-credit" />
                <NumField label="Property Value" value={borrower.property_value} onChange={(v) => setField("property_value", v)} testid="demo-prop-val" />
                <NumField label="Loan Amount" value={borrower.loan_amount} onChange={(v) => setField("loan_amount", v)} testid="demo-loan" />

                <SelectField
                  label="Document Authenticity"
                  value={borrower.document_authenticity}
                  onChange={(v) => setField("document_authenticity", v)}
                  testid="demo-auth"
                  options={[
                    { v: "passed", l: "Passed (clean docs)" },
                    { v: "failed", l: "Failed (tampering)" },
                  ]}
                />
                <SelectField
                  label="Employment"
                  value={borrower.employment_status}
                  onChange={(v) => setField("employment_status", v)}
                  testid="demo-employ"
                  options={[
                    { v: "employed", l: "Employed" },
                    { v: "self_employed", l: "Self-Employed" },
                    { v: "retired", l: "Retired" },
                    { v: "other", l: "Other" },
                  ]}
                />

                {/* Live preview metrics */}
                <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                  <Metric label="DTI" value={`${dti.toFixed(1)}%`} hot={dti > 55} />
                  <Metric label="LTV" value={`${ltv.toFixed(1)}%`} hot={ltv > 97} />
                  <Metric label="Credit" value={borrower.self_reported_credit} hot={Number(borrower.self_reported_credit) < 500} />
                </div>
                <div className={`mt-3 text-center text-xs uppercase tracking-wider font-bold py-2 rounded-sm ${
                  willPassFirewall ? "bg-[#00A651]/10 text-[#00A651]" : "bg-[#ED1C24]/10 text-[#ED1C24]"
                }`}>
                  Firewall preview: {willPassFirewall ? "would PASS" : "would ROUTE to nurture"}
                </div>

                <button
                  onClick={runDemo}
                  data-testid="demo-run"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#00A651] hover:bg-[#008f45] text-white font-bold uppercase tracking-wider text-sm px-5 py-3 rounded-sm transition-colors"
                >
                  <Play size={16} /> Run Pipeline
                </button>
                {result && (
                  <button
                    onClick={replay}
                    data-testid="demo-replay"
                    className="mt-2 w-full inline-flex items-center justify-center gap-2 border border-gray-200 hover:border-black text-black font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded-sm transition-colors"
                  >
                    <RotateCcw size={14} /> Replay Animation
                  </button>
                )}
              </div>
            </section>

            {/* RIGHT — pipeline timeline */}
            <section className="lg:col-span-8">
              {!result ? (
                <EmptyState />
              ) : (
                <>
                  <ResultBanner result={result} progress={Math.min(playedIdx + 1, result.events.length)} total={result.events.length} />
                  <ol className="mt-6 space-y-3" data-testid="demo-events">
                    {result.events.map((ev, i) => (
                      <EventRow
                        key={`${ev.agent}-${i}`}
                        ev={ev}
                        revealed={i <= playedIdx}
                        active={i === playedIdx && playing}
                      />
                    ))}
                  </ol>
                </>
              )}
            </section>
          </div>

          {/* EXPLAINER */}
          <section className="mt-16">
            <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#00A651]">How it Works</p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-black mt-2">The firewall logic — in plain English.</h2>
            <Divider className="my-4" />
            <div className="grid md:grid-cols-3 gap-4 mt-6">
              <ExplainerCard
                color="#ED1C24"
                badge="Rule 1"
                title="Document authenticity"
                body="If Agent 3 flags tampering or metadata mismatch, the pipeline stops. No premium APIs are called."
              />
              <ExplainerCard
                color="#E88C2F"
                badge="Rule 2"
                title="Debt-to-Income"
                body="If Agent 4 calculates DTI above 55%, the lead is routed to the assets-seasoning nurture track."
              />
              <ExplainerCard
                color="#F26522"
                badge="Rule 3"
                title="Credit floor"
                body="If self-reported credit is below 500, the lead is assigned a credit-roadmap track with 30-day check-ins."
              />
            </div>
            <p className="font-sub italic text-gray-600 mt-6 max-w-3xl">
              When all three rules pass, Agent 5 promotes the borrower to Agent 6 (premium APIs)
              and Agent 7 (HITL liaison). When any rule fails, Agent 8 takes over with a
              tailored long-term nurture plan and hot-routes the borrower back to Agent 1
              when milestones are met.
            </p>
          </section>
        </div>
      </main>
      <BrandStripe thickness="h-1.5" />
    </div>
  );
}

/* ---------- Subcomponents ---------- */

function Field({ label, value, onChange, testid }) {
  return (
    <div className="mt-3">
      <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-1 rounded-sm" />
    </div>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div className="mt-3">
      <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">{label}</Label>
      <Input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        data-testid={testid}
        className="mt-1 rounded-sm"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div className="mt-3">
      <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => (<SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>))}
        </SelectContent>
      </Select>
    </div>
  );
}

function Metric({ label, value, hot }) {
  return (
    <div className={`border rounded-sm py-2 ${hot ? "border-[#ED1C24] bg-[#ED1C24]/5" : "border-gray-200"}`}>
      <div className={`font-heading font-bold text-base ${hot ? "text-[#ED1C24]" : "text-black"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white border border-dashed border-gray-300 rounded-sm h-[520px] grid place-items-center text-center px-6">
      <div>
        <Sparkles className="mx-auto text-[#00A651]" size={36} />
        <h3 className="font-heading font-bold text-xl text-black mt-3">Run the pipeline</h3>
        <p className="font-sub italic text-gray-600 mt-2 max-w-md">
          Pick a preset or edit the borrower profile, then click <b>Run Pipeline</b> to watch all eight agents work in sequence.
        </p>
      </div>
    </div>
  );
}

function ResultBanner({ result, progress, total }) {
  const isNurture = result.final_route === "nurture";
  const c = isNurture ? "#F26522" : "#00A651";
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-5 border-l-4" style={{ borderLeftColor: c }} data-testid="demo-result-banner">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] font-bold" style={{ color: c }}>
            {isNurture ? "Routed to Nurture" : "Ready Loan Package"}
          </p>
          <h3 className="font-heading font-bold text-xl text-black mt-1">
            {result.borrower.first_name} {result.borrower.last_name}
            {result.nurture_track && <span className="text-gray-500"> · {result.nurture_track} track</span>}
          </h3>
          <p className="font-sub italic text-gray-600 mt-1 max-w-2xl">{result.summary}</p>
        </div>
        <div className="text-xs uppercase tracking-wider text-gray-500 font-bold">
          Step <span className="text-black">{progress}</span> / {total}
        </div>
      </div>
      <div className="h-1 bg-gray-100 mt-4 rounded-full overflow-hidden">
        <motion.div
          className="h-full"
          style={{ backgroundColor: c }}
          animate={{ width: `${(progress / total) * 100}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>
    </div>
  );
}

function EventRow({ ev, revealed, active }) {
  const Icon = AGENT_ICONS[ev.agent] || Sparkles;
  const status = STATUS_ICONS[ev.status] || STATUS_ICONS.pending;
  const SIcon = status.Icon;
  return (
    <motion.li
      initial={{ opacity: 0, x: 24 }}
      animate={{
        opacity: revealed ? 1 : 0.25,
        x: revealed ? 0 : 12,
        scale: active ? 1.01 : 1,
      }}
      transition={{ duration: 0.4 }}
      className={`bg-white border rounded-sm p-5 flex gap-5 items-start ${
        active ? "border-2 shadow-md" : "border-gray-200"
      } ${ev.status === "skipped" ? "opacity-70" : ""}`}
      style={{ borderColor: active ? ev.color : undefined }}
      data-testid={`event-${ev.agent}`}
    >
      <div
        className="w-12 h-12 grid place-items-center rounded-sm flex-shrink-0"
        style={{ backgroundColor: `${ev.color}15`, color: ev.color }}
      >
        <Icon size={22} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="font-heading font-bold text-[10px] px-2 py-0.5 rounded-sm text-white tracking-wider"
            style={{ backgroundColor: ev.color }}
          >
            AGENT {ev.agent}
          </span>
          <span className="font-heading font-bold text-sm text-black">{ev.name}</span>
          <SIcon size={16} style={{ color: status.c }} className={ev.status === "pending" ? "animate-spin" : ""} />
        </div>
        <p className="font-heading font-bold text-base text-black mt-1.5">{ev.title}</p>
        <p className="font-sub italic text-gray-600 text-sm mt-1">{ev.detail}</p>
        {Object.keys(ev.metrics || {}).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {Object.entries(ev.metrics).map(([k, v]) => (
              <span key={k} className="text-[10px] uppercase tracking-wider bg-gray-100 text-gray-700 px-2 py-1 rounded-sm">
                {k.replace(/_/g, " ")}: <b className="text-black ml-1">{String(Array.isArray(v) ? v.join(", ") : v)}</b>
              </span>
            ))}
          </div>
        )}
      </div>
      {revealed && active && ev.agent < 8 && (
        <ArrowRight className="text-gray-400 hidden md:block self-center" size={18} />
      )}
    </motion.li>
  );
}

function ExplainerCard({ color, badge, title, body }) {
  return (
    <div className="bg-white border border-gray-200 p-5 rounded-sm border-t-4" style={{ borderTopColor: color }}>
      <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color }}>{badge}</p>
      <h3 className="font-heading font-bold text-lg text-black mt-1">{title}</h3>
      <p className="text-sm text-gray-700 mt-2 leading-relaxed">{body}</p>
    </div>
  );
}
